import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FleetDutyCycleFigureService } from 'src/app/_services/fleet-duty-cycle-figure.service';
import { ALL_BATTERY_TYPES, BATTERY_TYPE_COLORS, BatteryTypeOption, toBatteryTypeFilter } from 'src/app/_shared/utils/battery-type';
import { CelsiusToFahrenheitPipe } from 'src/app/_shared/pipes/celsius-to-fahrenheit.pipe';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { RangeYPipe } from 'src/app/_shared/pipes/range-y.pipe';

@Component({
  selector: 'app-temperature-comfort-zone-chart',
  templateUrl: './temperature-comfort-zone-chart.component.html',
  styleUrls: ['./temperature-comfort-zone-chart.component.scss']
})
export class TemperatureComfortZoneChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  @Input() selectedBatteryTypes: BatteryTypeOption[] = [];
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  private readonly celsiusToFahrenheitPipe = new CelsiusToFahrenheitPipe()
  private readonly rangeYPipe = new RangeYPipe();

  constructor(
    private readonly fleetDutyCycleFigureService: FleetDutyCycleFigureService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  
  ngOnChanges(): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.fleetDutyCycleFigureService.getTemperatureComfortZoneUsageFleet(this.fleetId, toBatteryTypeFilter(this.selectedBatteryTypes)).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          this.plotTemperatureComfortZoneChart(datapoints);
        },
        error: (error) => {
          console.error('Error fetching Temperature Comfort Zone Usage Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        },
        complete: () => {
          this.dataLoading = false;
        }
      }
    )
  }
  
  plotTemperatureComfortZoneChart(datapoints: any) {
    const data: any[] = [];
    const allTemperatures: any[] = [];

    const entries = Object.values(datapoints ?? {}).flatMap((value: any) =>
      Array.isArray(value?.timeInComfortZone) ? value.timeInComfortZone : []);

    this.selectedBatteryTypes.forEach((option, index) => {
      const color = BATTERY_TYPE_COLORS[index % BATTERY_TYPE_COLORS.length];
      const plotData = entries.filter((item: any) =>
        (item?.batteryType || ALL_BATTERY_TYPES) === option.batteryType && item.operationTime != 0);
      const xValues = this.getConvertedValuesByUnit(plotData.map((dataObject: any) => dataObject.batteryTemp));
      const yValues = plotData.map((dataObject: any) => dataObject.operationTime);

      allTemperatures.push(...xValues);

      const totalOpTime = yValues.reduce((acc: any, operationTime: any) => acc + operationTime, 0);
      const normalizedYValues = yValues.map((operationTime: number) => (operationTime / totalOpTime) * 100); // Normalize operation time

      for (let i = 0; i < xValues.length - 1; i++) {
        data.push({
          x: [xValues[i], xValues[i + 1]],
          y: [normalizedYValues[i], normalizedYValues[i + 1]],
          type: 'scatter',
          mode: 'lines',
          line: { color },
          legendgroup: option.batteryType,
          showlegend: i === 0,
          name: option.label,
        });
      }
    });

    const minTemp = Math.min(...allTemperatures);
    const maxTemp = Math.max(...allTemperatures);

    const allYValues = data.flatMap(trace => trace.y);
    const [yMin, yMax] = this.rangeYPipe.transform(allYValues, 5);

    if (allTemperatures.length === 0) {
      return;
    }

    const linspace = (start: number, end: number, num: number) => {
      if (num === 1) return [start];
      const step = (end - start) / (num - 1);
      return Array.from({ length: num }, (_, i) => start + i * step);
    };

    const gradientSteps = 200;
    const xGradient = linspace(minTemp, maxTemp, gradientSteps);
    const gradientRow = linspace(0, 1, gradientSteps);
    const gradientTrace = {
      x: xGradient,
      y: [0, 100],
      z: [gradientRow, gradientRow],
      type: 'heatmap',
      colorscale: [
        [0, '#56B0FF'],
        [0.33, '#FFDF95'],
        [0.66, '#5EBD82'],
        [1, '#FF8787']
      ],
      showscale: false,
      hoverinfo: 'skip',
      opacity: 0.25,
      zmin: 0,
      zmax: 1
    };

    data.unshift(gradientTrace);

    const layout = {
      title: 'Temperature comfort zone usage',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      xaxis: {
        title: `Battery Temperature [${this.getTemperatureUnit()}]`,
      },
      yaxis: {
        title: 'Operation time [%]',
        range: [yMin, yMax],
      },
      showlegend: true,
      legend: {
        orientation: 'h', 
        y: -0.3, 
        xanchor: 'center', 
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('temperature-comfort-zone-chart', data, layout);
  }

  getConvertedValuesByUnit(arr: number[]): number[] {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? arr : arr.map(val => this.celsiusToFahrenheitPipe.transform(val));
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? arr.map(val => this.celsiusToFahrenheitPipe.transform(val))
        : arr;
    }
  }

  getThresholdValue(celsiusValue: number, fahrenheitValue: number): number {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? celsiusValue : fahrenheitValue;
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? fahrenheitValue
        : celsiusValue;
    }
  }

  getTemperatureUnit(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? '°C' : '°F';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? '°F'
        : '°C';
    }
  }
}
